import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Box, Alert, Snackbar } from '@mui/material'
import { productService, categoryService, divisionService } from '../services'
import { useProductList } from '../hooks/useProductList'
import { useListQueryState } from '../hooks/useListQueryState'
import { useAllPaginatedItems } from '../hooks/useAllPaginatedItems'
import {
  AVAILABLE_CSV_COLUMNS,
  buildEditFormData,
  buildProductDetailPath,
  buildProductManageSearchParams,
  buildProductUpdatePayload,
  getEditProductFieldErrors,
  isPackSizeApiErrorMessage,
  PACK_SIZE_FIELD_ERROR,
  getProductManageReturnPath,
  parseProductManageSearchParams,
  productManageHasExplicitParams,
  toProductApiParams,
} from '../utils/productManagementUtils'
import ProductManagementHeader from '../components/product-management/ProductManagementHeader'
import ProductSearchBar from '../components/product-management/ProductSearchBar'
import ProductTable from '../components/product-management/ProductTable'
import ViewProductDialog from '../components/product-management/ViewProductDialog'
import EditProductDialog from '../components/product-management/EditProductDialog'
import DeleteProductDialog from '../components/product-management/DeleteProductDialog'
import ProductInventoryViewDialog from '../components/product-management/ProductInventoryViewDialog'
import ExcelDownloadDialog from '../components/product-management/ExcelDownloadDialog'

const PRODUCTS_ROUTE_KEY = 'products.manage'

const parseProductFilters = (sp) => parseProductManageSearchParams(sp)
const buildProductFilters = (filters) => buildProductManageSearchParams(filters)
const productLabelFromFilters = (filters) =>
  (filters.q || '').trim() ? `“${filters.q.trim()}”` : 'Product filters'
const productIsDefaultFilters = (filters) =>
  !(filters.q || '').trim() &&
  (filters.page || 0) === 0 &&
  (filters.limit || 10) === 10

const ProductManagementPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const {
    filters,
    setFilters,
    resetFilters,
    applyRecent,
    recent,
    isDefault,
  } = useListQueryState({
    routeKey: PRODUCTS_ROUTE_KEY,
    parse: parseProductFilters,
    build: buildProductFilters,
    hasExplicitParams: productManageHasExplicitParams,
    labelFromFilters: productLabelFromFilters,
    isDefaultFilters: productIsDefaultFilters,
  })

  const {
    products,
    loading,
    error,
    setError,
    page,
    rowsPerPage,
    totalCount,
    fetchProducts,
  } = useProductList(filters)

  const [toastOpen, setToastOpen] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [successMessage, setSuccessMessage] = useState(null)

  const fetchAllCategories = useCallback((params) => categoryService.getCategories(params), [])
  const fetchAllDivisions = useCallback((params) => divisionService.getDivisions(params), [])
  const { items: categories } = useAllPaginatedItems(fetchAllCategories)
  const { items: divisions } = useAllPaginatedItems(fetchAllDivisions)

  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [inventoryDialogOpen, setInventoryDialogOpen] = useState(false)
  const [csvDialogOpen, setCsvDialogOpen] = useState(false)

  const [selectedProduct, setSelectedProduct] = useState(null)
  const [editFormData, setEditFormData] = useState({})
  const [actionLoading, setActionLoading] = useState(false)
  const [actionError, setActionError] = useState(null)
  const [editFieldErrors, setEditFieldErrors] = useState({})
  const [showCascadeOption, setShowCascadeOption] = useState(false)
  const [deleteError, setDeleteError] = useState(null)

  const [csvDownloading, setCsvDownloading] = useState(false)
  const [csvLimit, setCsvLimit] = useState(100)
  const [csvDownloadAll, setCsvDownloadAll] = useState(true)
  const [includeBarcodes, setIncludeBarcodes] = useState(false)
  const [includePricing, setIncludePricing] = useState(false)
  const [selectedColumns, setSelectedColumns] = useState(AVAILABLE_CSV_COLUMNS.map((col) => col.id))

  const applySearchQuery = useCallback(
    (q) => {
      setFilters({ q, page: 0 }, { commitRecent: true })
    },
    [setFilters],
  )

  const handleProductPageChange = useCallback(
    (_event, newPage) => {
      setFilters({ page: newPage })
    },
    [setFilters],
  )

  const handleProductRowsPerPageChange = useCallback(
    (event) => {
      setFilters({
        page: 0,
        limit: parseInt(event.target.value, 10) || 10,
      })
    },
    [setFilters],
  )

  const openEditDialog = useCallback((product) => {
    setSelectedProduct(product)
    setEditFormData(buildEditFormData(product))
    setEditFieldErrors({})
    setActionError(null)
    setEditDialogOpen(true)
  }, [])

  useEffect(() => {
    if (location.state?.editProductId && products.length > 0) {
      const productToEdit = products.find((p) => p.id === location.state.editProductId)
      if (productToEdit) {
        openEditDialog(productToEdit)
        navigate(location.pathname, { replace: true, state: {} })
      }
    }
  }, [location.state, products, navigate, location.pathname, openEditDialog])

  const handleNavigateToProduct = useCallback(
    (productId) => {
      const returnTo = getProductManageReturnPath(
        buildProductManageSearchParams(filters),
      )
      navigate(buildProductDetailPath(productId, returnTo), {
        state: { returnTo },
      })
    },
    [navigate, filters],
  )

  const handleBulkBarcodeUpload = useCallback(() => {
    navigate('/products/barcodes/upload')
  }, [navigate])

  const handleBulkEdit = useCallback(() => {
    navigate('/products/bulk-edit')
  }, [navigate])

  const handleCopySku = useCallback(async (sku) => {
    try {
      await navigator.clipboard.writeText(sku)
      setToastMessage('Article Code copied to clipboard!')
      setToastOpen(true)
    } catch {
      const textArea = document.createElement('textarea')
      textArea.value = sku
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()
      try {
        document.execCommand('copy')
        setToastMessage('Article Code copied to clipboard!')
        setToastOpen(true)
      } catch (err) {
        console.error('Failed to copy article code:', err)
        setToastMessage('Failed to copy Article Code')
        setToastOpen(true)
      }
      document.body.removeChild(textArea)
    }
  }, [])

  const handleViewClick = useCallback((product) => {
    setSelectedProduct(product)
    setViewDialogOpen(true)
  }, [])

  const handleViewClose = useCallback(() => {
    setViewDialogOpen(false)
    setSelectedProduct(null)
  }, [])

  const handleEditClick = useCallback(
    (product) => {
      openEditDialog(product)
    },
    [openEditDialog],
  )

  const handleEditClose = useCallback(() => {
    setEditDialogOpen(false)
    setSelectedProduct(null)
    setEditFormData({})
    setActionError(null)
    setEditFieldErrors({})
  }, [])

  const handleEditChange = useCallback((field, value) => {
    setEditFormData((prev) => ({ ...prev, [field]: value }))
    if (field === 'pack_size') {
      setEditFieldErrors((prev) => {
        if (!prev.pack_size) return prev
        const next = { ...prev }
        delete next.pack_size
        return next
      })
    }
  }, [])

  const handleSaveEdit = useCallback(async () => {
    try {
      setActionLoading(true)
      setActionError(null)
      setEditFieldErrors({})

      if (!editFormData.source) {
        setActionError('Source is required')
        return
      }
      if (editFormData.source === 'shwapno' && !String(editFormData.sku || '').trim()) {
        setActionError('Article Code is required for shwapno products')
        return
      }

      const fieldErrors = getEditProductFieldErrors(editFormData)
      if (Object.keys(fieldErrors).length > 0) {
        setEditFieldErrors(fieldErrors)
        return
      }

      await productService.updateProduct(
        selectedProduct.id,
        buildProductUpdatePayload(editFormData),
      )

      setSuccessMessage('Product updated successfully')
      setTimeout(() => setSuccessMessage(null), 3000)
      handleEditClose()
      fetchProducts()
    } catch (err) {
      console.error('Error updating product:', err)
      const message =
        err.response?.data?.message || err.message || 'Failed to update product'
      if (isPackSizeApiErrorMessage(message)) {
        setEditFieldErrors({ pack_size: PACK_SIZE_FIELD_ERROR })
        return
      }
      setActionError(message)
    } finally {
      setActionLoading(false)
    }
  }, [editFormData, selectedProduct, handleEditClose, fetchProducts])

  const handleDeleteClick = useCallback((product) => {
    setSelectedProduct(product)
    setDeleteConfirmOpen(true)
  }, [])

  const handleDeleteConfirm = useCallback(
    async (cascade = false) => {
      try {
        setActionLoading(true)
        setActionError(null)
        setDeleteError(null)

        const response = cascade
          ? await productService.deleteProduct(selectedProduct.id, { cascade: true })
          : await productService.deleteProduct(selectedProduct.id)

        if (response && response.success) {
          setSuccessMessage(
            cascade
              ? 'Product and all related data deleted successfully'
              : 'Product deleted successfully',
          )
          setTimeout(() => setSuccessMessage(null), 3000)
          setDeleteConfirmOpen(false)
          setSelectedProduct(null)
          setShowCascadeOption(false)
          setDeleteError(null)
          fetchProducts()
        } else {
          setActionError(response?.message || 'Unknown error occurred')
        }
      } catch (err) {
        console.error('Error deleting product:', err)
        const errorData = err.response?.data

        if (err.response?.status === 409 && errorData?.conflict && !cascade) {
          setDeleteError(errorData.message)
          setShowCascadeOption(true)
        } else {
          setActionError(errorData?.message || 'Failed to delete product')
        }
      } finally {
        setActionLoading(false)
      }
    },
    [selectedProduct, fetchProducts],
  )

  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirmOpen(false)
    setSelectedProduct(null)
    setShowCascadeOption(false)
    setDeleteError(null)
    setActionError(null)
  }, [])

  const handleInventoryClick = useCallback((product) => {
    setSelectedProduct(product)
    setInventoryDialogOpen(true)
  }, [])

  const handleInventoryClose = useCallback(() => {
    setInventoryDialogOpen(false)
    setSelectedProduct(null)
  }, [])

  const handleOpenCsvDialog = useCallback(() => {
    setCsvDialogOpen(true)
    setCsvDownloadAll(true)
    setCsvLimit(100)
    setIncludeBarcodes(false)
    setIncludePricing(false)
    setActionError(null)
  }, [])

  const handleCloseCsvDialog = useCallback(() => {
    setCsvDialogOpen(false)
    setCsvDownloading(false)
  }, [])

  const handleToggleAllColumns = useCallback(() => {
    setSelectedColumns((prev) =>
      prev.length === AVAILABLE_CSV_COLUMNS.length ? [] : AVAILABLE_CSV_COLUMNS.map((col) => col.id),
    )
  }, [])

  const handleToggleColumn = useCallback((columnId) => {
    setSelectedColumns((prev) =>
      prev.includes(columnId) ? prev.filter((id) => id !== columnId) : [...prev, columnId],
    )
  }, [])

  const handleCsvDownloadAllChange = useCallback((checked) => {
    setCsvDownloadAll(checked)
    if (!checked) setCsvLimit(100)
  }, [])

  const handleCsvLimitChange = useCallback((value) => {
    const parsed = parseInt(value, 10) || 0
    setCsvLimit(parsed > 0 ? Math.min(parsed, 10000) : 100)
  }, [])

  const handleStartExcelDownload = useCallback(async () => {
    if (selectedColumns.length === 0) {
      setActionError('Please select at least one column to download')
      return
    }

    try {
      setCsvDownloading(true)
      setActionError(null)

      const payload = {
        selectedColumns,
        downloadAll: csvDownloadAll,
        includeBarcodes,
        includePricing,
      }

      if (!csvDownloadAll) {
        payload.limit = csvLimit
      }

      const apiFilters = toProductApiParams({ ...filters, page: 0 })
      if (apiFilters.search) {
        payload.search = apiFilters.search
      }

      const response = await productService.createDownloadJob(payload)
      setCsvDialogOpen(false)
      setSuccessMessage(
        response.jobId
          ? `Export started (Job ID ${response.jobId}). Opening Product Downloads…`
          : response.message || 'Export started. Opening Product Downloads…',
      )
      navigate('/products/downloads')
    } catch (err) {
      console.error('Error starting Excel download:', err)
      const apiMessage = err.response?.data?.message
      setActionError(
        (Array.isArray(apiMessage) ? apiMessage.join(', ') : apiMessage) ||
          'Could not start export. Check your connection and try again.',
      )
    } finally {
      setCsvDownloading(false)
    }
  }, [
    selectedColumns,
    csvDownloadAll,
    csvLimit,
    includeBarcodes,
    includePricing,
    filters,
    navigate,
  ])

  return (
    <Box>
      <ProductManagementHeader
        totalCount={totalCount}
        onBulkBarcodeUpload={handleBulkBarcodeUpload}
        onBulkEdit={handleBulkEdit}
        onDownloadExcel={handleOpenCsvDialog}
        onOpenDownloads={() => navigate('/products/downloads')}
      />

      <ProductSearchBar
        appliedSearch={filters.q || ''}
        onApplySearchQuery={applySearchQuery}
        onClearFilters={resetFilters}
        isDefault={isDefault}
        recent={recent}
        onApplyRecent={applyRecent}
      />

      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <ProductTable
        products={products}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={totalCount}
        onPageChange={handleProductPageChange}
        onRowsPerPageChange={handleProductRowsPerPageChange}
        onView={handleViewClick}
        onEdit={handleEditClick}
        onDelete={handleDeleteClick}
        onInventory={handleInventoryClick}
        onCopySku={handleCopySku}
        onNavigateToProduct={handleNavigateToProduct}
      />

      <ViewProductDialog
        open={viewDialogOpen}
        product={selectedProduct}
        onClose={handleViewClose}
      />

      <EditProductDialog
        open={editDialogOpen}
        formData={editFormData}
        categories={categories}
        divisions={divisions}
        loading={actionLoading}
        error={actionError}
        onClose={handleEditClose}
        onSave={handleSaveEdit}
        onChange={handleEditChange}
        onClearError={() => setActionError(null)}
        fieldErrors={editFieldErrors}
      />

      <DeleteProductDialog
        open={deleteConfirmOpen}
        product={selectedProduct}
        loading={actionLoading}
        actionError={actionError}
        deleteError={deleteError}
        showCascadeOption={showCascadeOption}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />

      <ProductInventoryViewDialog
        open={inventoryDialogOpen}
        product={selectedProduct}
        onClose={handleInventoryClose}
      />

      <ExcelDownloadDialog
        open={csvDialogOpen}
        appliedSearchQuery={filters.q || ''}
        totalCount={totalCount}
        downloading={csvDownloading}
        error={actionError}
        downloadAll={csvDownloadAll}
        rowLimit={csvLimit}
        selectedColumns={selectedColumns}
        includeBarcodes={includeBarcodes}
        includePricing={includePricing}
        onClose={handleCloseCsvDialog}
        onSubmit={handleStartExcelDownload}
        onClearError={() => setActionError(null)}
        onDownloadAllChange={handleCsvDownloadAllChange}
        onRowLimitChange={handleCsvLimitChange}
        onToggleAllColumns={handleToggleAllColumns}
        onToggleColumn={handleToggleColumn}
        onIncludeBarcodesChange={setIncludeBarcodes}
        onIncludePricingChange={setIncludePricing}
      />

      <Snackbar
        open={toastOpen}
        autoHideDuration={3000}
        onClose={() => setToastOpen(false)}
        message={toastMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  )
}

export default ProductManagementPage
